import React, { useState } from 'react'
import type { InsightOut } from '../types'

interface InsightCardProps {
  insight: InsightOut
  compact?: boolean
}

const CATEGORY_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  tempo: { bg: 'rgba(96, 165, 250, 0.12)', border: 'rgba(96, 165, 250, 0.3)', text: 'var(--blue)' },
  defense: { bg: 'rgba(74, 222, 128, 0.12)', border: 'rgba(74, 222, 128, 0.3)', text: 'var(--green)' },
  attack: { bg: 'rgba(251, 146, 60, 0.12)', border: 'rgba(251, 146, 60, 0.3)', text: 'var(--orange)' },
  set_pieces: { bg: 'rgba(167, 139, 250, 0.12)', border: 'rgba(167, 139, 250, 0.3)', text: 'var(--purple)' },
  cards: { bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.3)', text: 'var(--yellow)' },
  form: { bg: 'rgba(45, 212, 191, 0.12)', border: 'rgba(45, 212, 191, 0.3)', text: 'var(--teal)' },
  h2h: { bg: 'rgba(244, 114, 182, 0.12)', border: 'rgba(244, 114, 182, 0.3)', text: 'var(--pink)' },
  anomaly: { bg: 'rgba(251, 113, 133, 0.12)', border: 'rgba(251, 113, 133, 0.3)', text: 'var(--red)' },
}

const DEFAULT_STYLE = { bg: 'rgba(255,255,255,0.05)', border: 'var(--border)', text: 'var(--muted)' }

export function InsightCard({ insight, compact = false }: InsightCardProps) {
  const [expanded, setExpanded] = useState(false)

  const style = CATEGORY_STYLES[insight.category || ''] || DEFAULT_STYLE
  const hasEvidence = insight.evidence && Object.keys(insight.evidence).length > 0

  if (compact) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.375rem 0.625rem',
          background: style.bg,
          border: `1px solid ${style.border}`,
          borderRadius: '6px',
          fontSize: '0.75rem',
        }}
      >
        {insight.icon && <span style={{ fontSize: '0.875rem' }}>{insight.icon}</span>}
        <span style={{ color: 'var(--fg)', fontWeight: 500 }}>{insight.headline}</span>
      </div>
    )
  }

  return (
    <div
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderRadius: '8px',
        padding: '0.75rem',
        marginBottom: '0.5rem',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        {insight.icon && (
          <span style={{ fontSize: '1.125rem', lineHeight: 1 }}>{insight.icon}</span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: 'var(--fg)',
              lineHeight: 1.3,
            }}
          >
            {insight.headline}
          </div>
          {insight.team_name && (
            <div
              style={{
                fontSize: '0.6875rem',
                color: style.text,
                marginTop: '0.125rem',
              }}
            >
              {insight.team_name}
            </div>
          )}
        </div>
        {hasEvidence && (
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--muted)',
              cursor: 'pointer',
              padding: '0.25rem',
              fontSize: '0.75rem',
            }}
          >
            {expanded ? 'Hide' : 'Evidence'}
          </button>
        )}
      </div>

      {/* Body */}
      {insight.body && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--muted)',
            marginTop: '0.5rem',
            lineHeight: 1.4,
          }}
        >
          {insight.body}
        </div>
      )}

      {/* Evidence Summary */}
      {insight.evidence_summary && (
        <div
          style={{
            fontSize: '0.6875rem',
            color: 'var(--muted)',
            marginTop: '0.375rem',
            opacity: 0.8,
          }}
        >
          {insight.evidence_summary}
        </div>
      )}

      {/* Expanded Evidence */}
      {expanded && insight.evidence && (
        <div
          style={{
            marginTop: '0.625rem',
            paddingTop: '0.5rem',
            borderTop: `1px solid ${style.border}`,
          }}
        >
          <div
            style={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--muted)',
              marginBottom: '0.375rem',
            }}
          >
            Supporting Data
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '0.375rem',
              fontSize: '0.6875rem',
            }}
          >
            {Object.entries(insight.evidence).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>
                  {key.replace(/_/g, ' ')}:
                </span>
                <span style={{ color: 'var(--fg)', fontWeight: 500 }}>
                  {typeof value === 'number' ? value.toLocaleString() : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default InsightCard
